import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { HealthCareFacilityModel } from "../../../data-model/healthFacility";

interface AddFacilityParams {
  facilityData: HealthCareFacilityModel;
  serviceTypesInput: string;
  userEmail?: string | null;
}

/**
 * Parses a comma-separated string into a trimmed, non-empty array of service types.
 *
 * @param input - Raw comma-separated service types entered by the user
 * @returns Array of trimmed, non-empty service type strings
 */
const parseServiceTypes = (input: string): string[] =>
  input
    .split(",")
    .map((serviceType) => serviceType.trim())
    .filter((serviceType) => serviceType.length > 0);

/**
 * Adds a new facility to the database
 */
export const addFacilityToDb = async (
  params: AddFacilityParams,
): Promise<string> => {
  const { facilityData, serviceTypesInput, userEmail } = params;

  const response = await FirestoreService.addDocument(
    FirestoreCollectionReference.healthcareFacility(),
    {
      ...facilityData,
      service_types: parseServiceTypes(serviceTypesInput),
      updated_by: userEmail ?? null,
    },
  );

  const newId = String((response as Record<string, unknown>)["id"] ?? "");

  await FirestoreService.updateDocument(
    FirestoreCollectionReference.healthcareFacility(),
    newId,
    {
      DocId: newId,
    },
  );

  return newId;
};

interface UpdateFacilityParams {
  facilityId: string;
  existingData: HealthCareFacilityModel | null;
  facilityData: HealthCareFacilityModel;
  selectedAuthStatusFilter: string[];
  serviceTypesInput: string;
  userEmail?: string | null;
}

/**
 * Updates an existing facility in the database
 */
export const updateFacilityInDb = async (
  params: UpdateFacilityParams,
): Promise<void> => {
  const {
    facilityId,
    existingData,
    facilityData,
    selectedAuthStatusFilter,
    serviceTypesInput,
    userEmail,
  } = params;

  await FirestoreService.updateDocument(
    FirestoreCollectionReference.healthcareFacility(),
    facilityId,
    {
      ...existingData,
      ...facilityData,
      auth_status_filter: selectedAuthStatusFilter,
      service_types: parseServiceTypes(serviceTypesInput),
      updated_by: userEmail ?? null,
      updated_at: new Date().toISOString(),
    } as HealthCareFacilityModel,
  );
};
